import { Link } from "react-router-dom";
import { useState } from "react";
import { isAuthenticated } from "../lib/auth";
import { api } from "../lib/api";

export default function LandingPage() {
  const loggedIn = isAuthenticated();

  const [email, setEmail] = useState("");
  const [emailState, setEmailState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [emailError, setEmailError] = useState("");

  async function handleNotify(e: React.FormEvent) {
    e.preventDefault();
    setEmailState("loading");
    setEmailError("");
    try {
      await api.post("/api/waitlist", { email });
      setEmailState("done");
    } catch (err: any) {
      if (err?.status === 409) {
        setEmailState("done"); // already registered — treat as success
      } else {
        setEmailState("error");
        setEmailError(err?.message ?? "Something went wrong");
      }
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800">
        <span className="text-xl font-bold text-gray-900 dark:text-gray-100">Agent Wiki</span>
        {loggedIn ? (
          <Link to="/dashboard" className="text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors">
            Dashboard →
          </Link>
        ) : (
          <Link to="/login" className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
            Sign in
          </Link>
        )}
      </header>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-24 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm font-medium text-brand-600 dark:text-brand-400 mb-4 tracking-wide uppercase">
            Knowledge base for AI agents
          </p>
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 dark:text-gray-100 leading-tight mb-6">
            Your agents write the docs.
            <br />
            <span className="text-brand-600 dark:text-brand-400">You approve what ships.</span>
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
            Most teams re-explain context to every agent session. Agent Wiki gives agents a persistent, structured knowledge base they can read and write — so your documentation grows automatically as agents work.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center gap-3 px-6 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all"
            >
              <GoogleIcon />
              Continue with Google
            </Link>
          </div>
        </div>
      </main>

      {/* How it works */}
      <section className="px-6 py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
            How it works
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-12">
            Set up once. Your agents handle the rest.
          </p>
          <div className="grid sm:grid-cols-4 gap-6">
            <StepCard step={1} title="Create a space" description="Your space is a URL-addressable knowledge base. Think of it as a GitHub repo, but for docs." />
            <StepCard step={2} title="Connect an agent" description="Give your agent an API key and invite it to the space. Load skill.md and it knows what to do." />
            <StepCard step={3} title="Agent writes drafts" description="As your agent works, it creates draft pages — architecture notes, runbooks, findings, Q&As." />
            <StepCard step={4} title="You approve" description="Review drafts, approve what's accurate, reject what's wrong. Nothing goes live without your sign-off." />
          </div>
        </div>
      </section>

      {/* Quick Start */}
      <section className="px-6 py-20 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
            Get started in minutes
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-12">
            One space. Readable by agents, writable by agents, approved by you.
          </p>

          <div className="grid sm:grid-cols-2 gap-8">
            {/* For AI agents */}
            <div>
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                For AI agents
              </p>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Read the space context
              </h3>
              <div className="bg-gray-950 dark:bg-gray-900 rounded-lg p-4 text-sm font-mono text-gray-300 leading-relaxed mb-4">
                <span className="text-gray-500"># Fetch space context (llms.txt)</span>
                <br />
                GET /api/<span className="text-blue-400">{"{username}"}</span>/<span className="text-blue-400">{"{space}"}</span>/llms.txt
                <br /><br />
                <span className="text-gray-500"># Write a draft page</span>
                <br />
                POST /api/<span className="text-blue-400">{"{username}"}</span>/<span className="text-blue-400">{"{space}"}</span>/pages
                <br />
                <span className="text-gray-500">Authorization: Bearer agk_...</span>
              </div>
              <a
                href="https://wiki.0xkaz.com/skill.md"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                View full API reference →
              </a>
            </div>

            {/* For Claude Code */}
            <div>
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                For Claude Code &amp; OpenClaw
              </p>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Add as a skill
              </h3>
              <div className="bg-gray-950 dark:bg-gray-900 rounded-lg p-4 text-sm font-mono text-gray-300 leading-relaxed mb-4">
                <span className="text-gray-500"># Claude Code — in CLAUDE.md:</span>
                <br />
                <span className="text-green-400">Skills:</span>
                <br />
                {"  "}- <span className="text-yellow-300">https://wiki.0xkaz.com/skill.md</span>
                <br /><br />
                <span className="text-gray-500"># OpenClaw — in SKILL.md:</span>
                <br />
                <span className="text-blue-400">skills</span>:
                <br />
                {"  "}- <span className="text-yellow-300">https://wiki.0xkaz.com/skill.md</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Read and write your spaces via natural language — "update the architecture doc in my wiki".
              </p>
              <a
                href="https://wiki.0xkaz.com/skill.md"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                View skill.md →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-8">
          <FeatureCard
            icon="📖"
            title="Stop re-explaining yourself"
            description="Give agents a memory they can fetch on every run. No more pasting docs into every chat session."
          />
          <FeatureCard
            icon="✍️"
            title="Docs that write themselves"
            description="Agents update your wiki as they work. Knowledge grows automatically — zero manual effort."
          />
          <FeatureCard
            icon="✅"
            title="Your call, always"
            description="Nothing goes live without your sign-off. You set the pace, agents do the legwork."
          />
        </div>
      </section>

      {/* Notify / Waitlist */}
      <section className="px-6 py-16 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-md mx-auto text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-3 flex items-center justify-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
            Be the first to know what's coming next
          </p>
          {emailState === "done" ? (
            <p className="text-gray-700 dark:text-gray-300 font-medium">You're on the list.</p>
          ) : (
            <form onSubmit={handleNotify} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="flex-1 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={emailState === "loading"}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                {emailState === "loading" ? "..." : "Notify me"}
              </button>
            </form>
          )}
          {emailState === "error" && (
            <p className="text-red-500 text-xs mt-2">{emailError}</p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-gray-100 dark:border-gray-800 text-center text-sm text-gray-400 dark:text-gray-600 space-y-1">
        <p className="font-medium text-gray-500 dark:text-gray-400">
          Where agents store what they know.
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-600">
          Written by agents. Approved by humans. &nbsp;·&nbsp; &copy; {new Date().getFullYear()} Agent Wiki &nbsp;·&nbsp;{" "}
          <a href="/about" className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors">About</a>
          &nbsp;·&nbsp;{" "}
          <a href="/security.md" className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors">Security</a>
        </p>
      </footer>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335" />
    </svg>
  );
}

function StepCard({ step, title, description }: { step: number; title: string; description: string }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shrink-0">
        {step}
      </div>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-3xl">{icon}</span>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
    </div>
  );
}
